/**
 * Paywall Variables Configuration
 *
 * Defines template variables available for paywall screens.
 * Variables are replaced at runtime with actual pricing data from Stripe.
 *
 * Usage:
 * - Type "@" in any paywall text field to see variable suggestions
 * - Variables are inserted as plain text (e.g., "@price")
 * - Runtime resolution happens in web-shell/src/services/payments/priceFormatter.ts
 *
 * @module reactFlow/config/paywallVariablesConfig
 */

/**
 * Paywall variable definition
 */
export interface PaywallVariable {
  /** Variable name without @ prefix */
  name: string;
  /** Display label in menu */
  label: string;
  /** Brief description of what the variable resolves to */
  description: string;
  /** Example value for preview */
  example: string;
  /** Category for grouping in UI */
  category: 'price' | 'duration' | 'trial' | 'intro';
}

/**
 * All available paywall variables
 *
 * These variables are resolved at runtime by the payment system.
 * The actual values come from Stripe product/price configuration.
 */
export const PAYWALL_VARIABLES: PaywallVariable[] = [
  // Price variables
  {
    name: 'price',
    label: 'Price',
    description: 'Just the price value',
    example: '$9.99',
    category: 'price',
  },
  {
    name: 'priceAndCurrency',
    label: 'Price & Currency',
    description: 'Price value with the currency sign',
    example: '$9.99 USD',
    category: 'price',
  },

  // Duration variables
  {
    name: 'duration',
    label: 'Duration',
    description: 'Just the duration value, without months/days suffix',
    example: '1',
    category: 'duration',
  },
  {
    name: 'interval',
    label: 'Interval',
    description: 'Duration with the suffix (months/days)',
    example: '1 month',
    category: 'duration',
  },

  // Trial variables
  {
    name: 'trialDays',
    label: 'Trial Days',
    description: 'Just the number of trial days',
    example: '7',
    category: 'trial',
  },
  {
    name: 'fullTrialDays',
    label: 'Full Trial Days',
    description: 'Number of trial days with "Day(s)" suffix',
    example: '7 Days',
    category: 'trial',
  },

  // Introductory offer variables
  {
    name: 'introPrice',
    label: 'Intro Price',
    description: 'Introductory price text from Stripe',
    example: '$0.99',
    category: 'intro',
  },
  {
    name: 'introProduct',
    label: 'Intro Product',
    description: 'Introductory product text from Stripe',
    example: 'Trial Offer',
    category: 'intro',
  },
  {
    name: 'introDuration',
    label: 'Intro Duration',
    description: 'Usage duration text from Stripe',
    example: '1 week',
    category: 'intro',
  },
];

/**
 * Screen types that support paywall pricing variables (@price, @duration, etc.)
 *
 * - ScreenBasicPaywall: V1 paywall screen
 * - Paywall: V2 paywall screen (StepKind)
 * - FlexibleLayout: V2 composable screen that may include ProductCatalog sections
 */
const PAYWALL_SCREEN_TYPES: ReadonlySet<string> = new Set([
  'ScreenBasicPaywall', // V1
  'Paywall',            // V2
  'FlexibleLayout',     // V2 — may contain ProductCatalog sections
]);

/**
 * Check if screen type supports paywall variables
 *
 * @param screenType - Screen type string from schema (V1 or V2 StepKind)
 * @returns true if paywall variables are available for this screen type
 */
export function supportsPaywallVariables(screenType: string | undefined): boolean {
  if (!screenType) return false;
  return PAYWALL_SCREEN_TYPES.has(screenType);
}

/**
 * Get variables grouped by category
 */
export function getVariablesByCategory(): Record<string, PaywallVariable[]> {
  return PAYWALL_VARIABLES.reduce(
    (acc, variable) => {
      if (!acc[variable.category]) {
        acc[variable.category] = [];
      }
      acc[variable.category].push(variable);
      return acc;
    },
    {} as Record<string, PaywallVariable[]>
  );
}

/**
 * Category display names
 */
export const CATEGORY_LABELS: Record<string, string> = {
  price: 'Price',
  duration: 'Duration',
  trial: 'Trial',
  intro: 'Intro Offer',
};
